import { Box, Button, IconButton, Modal, Tooltip } from '@mui/material';
import { useMutation } from '@tanstack/react-query';

import { Loader2, Plus, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { TagMemberType, TagType } from 'type/type';
interface Props {
  tagLabel: string;
  tagData: TagType;
}

interface ConfirmInterface {
  open: boolean;
  group: TagMemberType | null;
  method: 'delete' | 'add';
}
const MTGroupList = ({ tagLabel, tagData }: Props) => {
  const emptyConfirmState: ConfirmInterface = {
    open: false,
    group: null,
    method: 'delete',
  };
  const isPrimaryTag = tagLabel === tagData.name;
  const [localGroups, setLocalGroups] = useState<TagMemberType[]>([]);
  const [confirmState, setConfirmState] = useState<ConfirmInterface>(emptyConfirmState);
  const [showAllTagGroups, setShowAllTagGroups] = useState(false);
  useEffect(() => {
    setLocalGroups(tagData.members);
  }, [tagData]);
  const handleDeleteGroup = useMutation({
    mutationFn: () => {
      const newGroups = localGroups.filter(group => group.RowKey !== confirmState.group?.RowKey);
      setLocalGroups(newGroups);
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve({ status: 200, data: newGroups });
        }, 3000);
      });
    },
    onSuccess: () => {
      toast.success(`Group ${confirmState.group?.RowKey} deleted successfully`);
      setConfirmState(emptyConfirmState);
    },
    onError: () => {
      toast.error('Failed to delete group');
      setConfirmState(emptyConfirmState);
    },
  });
  const handleAddGroup = useMutation({
    mutationFn: (newGroup: TagMemberType) => {
      const newGroups = localGroups.concat(newGroup);
      setLocalGroups(newGroups);
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve({ status: 200, data: newGroups });
        }, 3000);
      });
    },
    onSuccess: () => {
      toast.success(`Group ${confirmState.group?.RowKey} add successfully`);
      setConfirmState(emptyConfirmState);
    },
    onError: () => {
      toast.error('Failed to delete group');
      setConfirmState(emptyConfirmState);
    },
  });
  const groupsToShow = showAllTagGroups && localGroups.length > 5 ? localGroups : localGroups.slice(0, 5);
  return (
    <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap', padding: '10px 20px', borderBottom: '1px solid var(--black01)' }}>
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '10px', fontWeight: 600, opacity: 0.7 }}>
        <p>{tagData.name.charAt(0).toUpperCase() + tagData.name.slice(1)}&apos;s Groups</p>
        <Button variant='text' sx={{ fontSize: '10px' }} onClick={() => setShowAllTagGroups(!showAllTagGroups)}>
          {showAllTagGroups ? 'Hide All' : 'Show All'}
        </Button>
      </Box>
      <Modal sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} open={confirmState.open} onClose={() => setConfirmState(emptyConfirmState)}>
        <Box sx={{ bgcolor: 'var(--white)', color: 'var(--black07)', width: '300px', backdropFilter: 'blur(10px)', borderRadius: '5px' }}>
          <h3 style={{ padding: '20px', width: '100%', backgroundColor: 'var(--black01)' }}>{confirmState.method.charAt(0).toUpperCase() + confirmState.method.slice(1)} Group</h3>
          <p style={{ padding: '20px', width: '100%' }}>
            Confirm to {confirmState.method} Group - <span style={{ fontWeight: 600 }}>{confirmState.group?.fn}</span> {confirmState.method === 'delete' ? 'from' : 'to'}{' '}
            <span style={{ fontWeight: 600 }}>{tagLabel.charAt(0).toUpperCase() + tagLabel.slice(1)} Tag</span>
          </p>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: '10px 20px' }}>
            <Button
              onClick={() => {
                setConfirmState(emptyConfirmState);
              }}
            >
              Cancel
            </Button>
            <Button
              variant={'contained'}
              onClick={() => {
                confirmState.method === 'delete' ? handleDeleteGroup.mutate() : handleAddGroup.mutate(confirmState.group as TagMemberType);
              }}
            >
              {handleDeleteGroup.isPending && <Loader2 size={20} style={{ marginRight: '10px' }} className='animated-rotation' />}
              {confirmState.method.charAt(0).toUpperCase() + confirmState.method.slice(1)}
            </Button>
          </Box>
        </Box>
      </Modal>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        {isPrimaryTag
          ? groupsToShow.map((group, index) => {
              return (
                <Tooltip key={index} title={group.fn}>
                  <Box sx={{ display: 'flex', alignItems: 'center', maxWidth: '200px', border: '1px solid var(--black01)', paddingLeft: '10px', borderRadius: '10px', ':hover': { backgroundColor: 'var(--black01)' } }}>
                    <p className={'text-overflow noselect'}>{group.fn}</p>
                    <IconButton
                      onClick={() => {
                        setConfirmState({
                          open: true,
                          group: group,
                          method: 'delete',
                        });
                      }}
                    >
                      <X size={20} color={'red'} />
                    </IconButton>
                  </Box>
                </Tooltip>
              );
            })
          : showAllTagGroups &&
            groupsToShow.map((group, index) => {
              return (
                <Tooltip key={index} title={group.fn}>
                  <Box sx={{ display: 'flex', alignItems: 'center', maxWidth: '200px', border: '1px solid var(--black01)', paddingLeft: '10px', borderRadius: '10px', ':hover': { backgroundColor: 'var(--black01)' } }}>
                    <p className={'text-overflow noselect'}>{group.fn}</p>
                    <IconButton
                      onClick={() => {
                        setConfirmState({
                          open: true,
                          group: group,
                          method: 'add',
                        });
                      }}
                    >
                      <Plus size={20} color={'green'} />
                    </IconButton>
                  </Box>
                </Tooltip>
              );
            })}

        {!showAllTagGroups && isPrimaryTag && localGroups.length > 5 && (
          <Box
            onClick={() => setShowAllTagGroups(!showAllTagGroups)}
            sx={{ display: 'flex', alignItems: 'center', border: '1px solid var(--black01)', padding: '10px', borderRadius: '10px', ':hover': { backgroundColor: 'var(--black01)' } }}
          >
            <p className={'text-overflow noselect'}>+{localGroups.length - 5}</p>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MTGroupList;
